import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Chip,
  alpha,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { useHistory } from "react-router-dom";

/**
 * Centered popup shown to guest users, styled like the Book Writing tool's
 * "Coming Soon" dialog.
 *
 * variant="signup" → guest clicked a locked tool/screen (e.g. Citation)
 * variant="limit"  → guest used up the free clicks (machineId based)
 */
export default function GuestSignupDialog({
  open,
  onClose,
  variant = "signup",
  toolName = "",
}) {
  const history = useHistory();
  const isLimit = variant === "limit";

  const handleSignup = () => {
    if (onClose) onClose();
    history.push("/signup");
  };

  const perks = [
    { icon: "🛠️", label: "36+ AI-powered tools" },
    { icon: "⚡", label: "Unlimited feature clicks" },
    { icon: "📄", label: "Save & manage documents" },
    { icon: "🤝", label: "Team collaboration" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        // Inline style so the global `.MuiDialog-paper` rule in
        // responsive.css cannot stretch this popup — keeps it the same
        // compact size as the Coming Soon dialog.
        style: { maxWidth: 560, width: "calc(100% - 64px)" },
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          border: `1px solid ${alpha("#0B4FE7", 0.25)}`,
          background: "linear-gradient(135deg, #ffffff, #eff6ff)",
        },
      }}
    >
      <Box sx={{ height: 4, background: "linear-gradient(90deg, #0B4FE7, #8b5cf6, #ec4899)" }} />
      <DialogContent sx={{ textAlign: "center", pt: 5, pb: 3, px: 4 }}>
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #0B4FE7, #8b5cf6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
            boxShadow: "0 8px 32px rgba(11,79,231,0.35)",
            animation: "bounce 2s ease-in-out infinite",
            "@keyframes bounce": {
              "0%, 100%": { transform: "translateY(0)" },
              "50%": { transform: "translateY(-8px)" },
            },
          }}
        >
          {isLimit ? (
            <TouchAppOutlinedIcon sx={{ fontSize: 44, color: "white" }} />
          ) : (
            <LockOutlinedIcon sx={{ fontSize: 44, color: "white" }} />
          )}
        </Box>
        <Chip
          label={isLimit ? "Free Limit Reached" : "Sign Up Required"}
          size="small"
          sx={{
            mb: 2,
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: 1.2,
            background: "linear-gradient(135deg, #0B4FE7, #8b5cf6)",
            color: "white",
            boxShadow: "0 2px 10px rgba(11,79,231,0.3)",
          }}
        />
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{
            mb: 1.5,
            background: "linear-gradient(135deg, #0B4FE7, #8b5cf6, #ec4899)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {isLimit ? "Free Clicks Exceeded" : "Synthium Creative Suite"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 380, mx: "auto", mb: 3, lineHeight: 1.8 }}
        >
          {isLimit
            ? "Please sign up — your free clicks are exceeded. Create a free Synthium Creative Suite account to keep using the AI tools without limits. ✨"
            : `Please sign up to Synthium Creative Suite${
                toolName ? ` to access ${toolName}` : " to access this feature"
              }. It only takes a minute and unlocks the full creative workspace. ✨`}
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 3, textAlign: "left" }}>
          {perks.map((feat) => (
            <Grid xs={6} key={feat.label}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.2,
                  borderRadius: 2,
                  background: alpha("#0B4FE7", 0.07),
                  border: `1px solid ${alpha("#0B4FE7", 0.18)}`,
                }}
              >
                <Typography sx={{ fontSize: 16 }}>{feat.icon}</Typography>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ lineHeight: 1.3 }}
                >
                  {feat.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 1 }}>
          Free to start — it only takes a minute ✨
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2.5,
            px: 3,
            textTransform: "none",
            fontWeight: 600,
            borderColor: alpha("#0B4FE7", 0.4),
            color: "#0B4FE7",
            "&:hover": { borderColor: "#0B4FE7", background: alpha("#0B4FE7", 0.06) },
          }}
        >
          Maybe Later
        </Button>
        <Button
          variant="contained"
          onClick={handleSignup}
          sx={{
            borderRadius: 2.5,
            px: 3,
            textTransform: "none",
            fontWeight: 700,
            background: "linear-gradient(135deg, #0B4FE7, #8b5cf6)",
            boxShadow: "0 4px 16px rgba(11,79,231,0.4)",
            "&:hover": {
              background: "linear-gradient(135deg, #0A46CC, #7c3aed)",
              boxShadow: "0 6px 20px rgba(11,79,231,0.5)",
            },
          }}
        >
          Sign Up Free
        </Button>
      </DialogActions>
    </Dialog>
  );
}
